// API Client per Gestionale Gruppo Impresa
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

class APIClient {
  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.currentUser = null;
    this._isLoggingOut = false;
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  _handleSessionExpired() {
    if (this._isLoggingOut) return;
    this._isLoggingOut = true;
    this.setToken(null);
    window.location.href = '/';
  }

  /**
   * Safe file download (22/02/2026, BUG FIX `insertBefore` definitivo).
   *
   * Root cause del bug `Failed to execute 'insertBefore'`:
   *   il `_triggerDownload` precedente metteva append+click in un
   *   `setTimeout(0)` ma il caller proseguiva immediatamente con
   *   `toast.success` e `setDownloading(false)`. React 19 riconciliava
   *   il portal sonner e il bottone PRIMA che il setTimeout eseguisse
   *   appendChild → al momento dell'append, il `body` era già stato
   *   ri-mutato e React perdeva traccia dei nodi → `insertBefore`
   *   error sui successivi render del toast.
   *
   * Fix: append → click → removeChild in UN UNICO FLUSH SINCRONO,
   *      senza setTimeout. Il browser scarica subito (nessuna race),
   *      il body torna allo stato originale prima che il caller
   *      possa schedulare ANY render successivo. Solo il revoke
   *      del blob URL resta differito (non interagisce col DOM).
   */
  _triggerDownload(blob, filename) {
    const url = URL.createObjectURL(blob);
    let a = null;
    try {
      a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.position = 'fixed';
      a.style.opacity = '0';
      a.style.pointerEvents = 'none';
      a.style.top = '-1000px';
      document.body.appendChild(a);
      a.click();
    } catch (e) {
      // Fallback: open in new tab. Mai propagare al chiamante.
      try { window.open(url, '_blank'); } catch { /* noop */ }
      // eslint-disable-next-line no-console
      console.warn('Anchor download fallback to window.open:', e);
    } finally {
      // Sync removal — stesso tick di append+click. Niente setTimeout.
      if (a && a.parentNode) {
        try { a.parentNode.removeChild(a); } catch { /* noop */ }
      }
      // Solo il revoke del blob URL può aspettare (no DOM mutation).
      setTimeout(() => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
      }, 5000);
    }
  }

  setCurrentUser(user) {
    this.currentUser = user;
  }

  getCurrentOperatorId() {
    return this.currentUser?.operatorId || null;
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  /**
   * Defensive body parser (11/06/2026 — bug fix `Errore nella richiesta`).
   *
   * Some host environments inject a global `fetch` wrapper (PostHog
   * session recorder, Emergent agent inspector, etc.) that ALREADY
   * consumes the Response body before our code sees it. The result is
   * `response.json()` / `response.clone()` throwing
   * "Response body is already used" → our `errorMessage` collapses to
   * the generic "Errore nella richiesta", hiding the real backend
   * `detail` (e.g. "Numero fattura '0027/P' già esistente").
   *
   * Workaround: when fetch can no longer give us the body, re-issue the
   * same request via XMLHttpRequest (which PostHog wraps DIFFERENTLY
   * and lets the body through). We only do this on error responses
   * (where the detail message matters) to avoid wasting a successful
   * round-trip.
   */
  async _readBodyDefensive(response, requestUrl, requestConfig) {
    // First try the modern path on a clone — works in 99% of cases.
    try {
      return await response.clone().json();
    } catch (_) {
      try {
        const raw = await response.clone().text();
        if (raw) {
          try { return JSON.parse(raw); } catch (_) { return { _rawText: raw }; }
        }
        return null;
      } catch (_) {
        // Body is gone (consumed by a global fetch wrapper). Fall back
        // to XHR so the user gets a real error message.
        return await this._xhrFallback(requestUrl, requestConfig);
      }
    }
  }

  _xhrFallback(url, config) {
    return new Promise((resolve) => {
      try {
        const xhr = new XMLHttpRequest();
        xhr.open(config.method || 'GET', url, true);
        const headers = config.headers || {};
        for (const k of Object.keys(headers)) {
          try { xhr.setRequestHeader(k, headers[k]); } catch (_) { /* noop */ }
        }
        xhr.onload = () => {
          try {
            const parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            resolve(parsed);
          } catch (_) {
            resolve(xhr.responseText ? { _rawText: xhr.responseText } : null);
          }
        };
        xhr.onerror = () => resolve(null);
        xhr.send(config.body || null);
      } catch (_) {
        resolve(null);
      }
    });
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      ...options,
      headers: this.getHeaders(),
    };

    try {
      const response = await fetch(url, config);
      
      // Check authentication first
      if (response.status === 401) {
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }

      const data = await this._readBodyDefensive(response, url, config);

      if (!response.ok) {
        const detailMsg =
          (data && typeof data === 'object' && data.detail) ||
          (data && typeof data === 'object' && data._rawText) ||
          null;
        const errorMessage = detailMsg || `Errore nella richiesta (HTTP ${response.status})`;
        const err = new Error(errorMessage);
        // Expose HTTP status on the Error so call sites can branch on it
        // (e.g. 404 vs 500). Without this, every non-200 surfaces as a
        // generic "Errore nella richiesta" string with no signal.
        err.status = response.status;
        throw err;
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // ==================== AUTH ====================
  async login(email, password) {
    // Direct fetch — do NOT use this.request() because 401 here means
    // "wrong credentials", not "session expired"
    const url = `${API_BASE_URL}/api/auth/login`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.detail || 'Credenziali non valide');
    }
    this._isLoggingOut = false; // Reset logout flag on successful login
    this.setToken(data.access_token);
    this.setCurrentUser(data.user);
    return data;
  }

  async requestPasswordReset(identifier) {
    const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.detail || 'Invio email non disponibile');
    }
    return data;
  }

  async confirmPasswordReset(token, newPassword) {
    const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    const data = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(data?.detail || 'Impossibile reimpostare la password');
    }
    return data;
  }

  async getCurrentUser() {
    const user = await this.request('/api/auth/me');
    this._isLoggingOut = false; // Valid session confirmed
    this.setCurrentUser(user);
    return user;
  }

  async linkOperator(operatorId) {
    const response = await this.request('/api/auth/link-operator', {
      method: 'POST',
      body: JSON.stringify({ operatorId }),
    });
    // Refresh user data to get updated operator link
    await this.getCurrentUser();
    return response;
  }

  async unlinkOperator() {
    const response = await this.request('/api/auth/unlink-operator', {
      method: 'POST',
    });
    // Refresh user data
    await this.getCurrentUser();
    return response;
  }

  logout() {
    this.setToken(null);
    this.currentUser = null;
  }

  // ==================== CLIENTS ====================
  async getClients() {
    return await this.request('/api/clients');
  }

  async createClient(client) {
    return await this.request('/api/clients', {
      method: 'POST',
      body: JSON.stringify(client),
    });
  }

  async updateClient(id, client) {
    return await this.request(`/api/clients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(client),
    });
  }

  async deleteClient(id) {
    return await this.request(`/api/clients/${id}`, {
      method: 'DELETE',
    });
  }

  // ─── Config Tables (30/06/2026) ─────────────────────────────────────
  // Tabelle di configurazione user-managed: "Tipo Pratica" + "Ente Coinvolto".
  // Caricate UNA VOLTA al boot dell'app (App.js) e cached in state.
  // Refetch on-demand dopo mutation (CRUD nella UI Impostazioni).
  async getConfigTables({ activeOnly = false } = {}) {
    const qs = activeOnly ? '?active_only=true' : '';
    return await this.request(`/api/config-tables${qs}`);
  }
  async createConfigEntry(entry) {
    // entry: {table, name, scope?}
    return await this.request('/api/config-tables', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  }
  async updateConfigEntry(id, patch) {
    // patch: {name?, scope?}
    return await this.request(`/api/config-tables/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  }
  async toggleConfigEntry(id) {
    return await this.request(`/api/config-tables/${id}/toggle`, {
      method: 'PATCH',
    });
  }
  async getConfigEntryUsage(id) {
    return await this.request(`/api/config-tables/${id}/usage`);
  }
  async deleteConfigEntry(id) {
    return await this.request(`/api/config-tables/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Real-time duplicate detection for client creation/edit (26/02/2026).
   * Body: { taxCode?, vatNumber?, fullName?, excludeId? }
   * Returns: { matches: [{id, fullName, taxCode, vatNumber, clientType, matchedOn}], blocking: bool }
   * `blocking` = true if at least one match is on taxCode or vatNumber.
   */
  async checkClientDuplicate(payload) {
    return await this.request('/api/clients/check-duplicate', {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  /**
   * Lookup di un CAP italiano → restituisce città + provincia (28/02/2026).
   * Returns: { cap, found, matches: [{city, province}], multiple }
   * Errors di rete vengono silenziati (la UI continua a permettere
   * inserimento manuale).
   */
  async lookupCap(cap) {
    const clean = String(cap || "").replace(/\D/g, "");
    if (clean.length !== 5) {
      return { cap: clean, found: false, matches: [], multiple: false };
    }
    return await this.request(`/api/utils/cap-lookup?cap=${encodeURIComponent(clean)}`);
  }

  // ==================== CASES ====================
  async getCases() {
    return await this.request('/api/cases');
  }

  async createCase(caseData) {
    return await this.request('/api/cases', {
      method: 'POST',
      body: JSON.stringify(caseData),
    });
  }

  async updateCase(id, caseData) {
    return await this.request(`/api/cases/${id}`, {
      method: 'PUT',
      body: JSON.stringify(caseData),
    });
  }

  async deleteCase(id) {
    return await this.request(`/api/cases/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== INVOICES ====================
  async getInvoices() {
    // 11/06/2026 — Esteso al registro "issued" che include sia
    // Fatture che Note di Credito. La logica di payment status del
    // backend e' aware delle CN; il frontend filtra ulteriormente per
    // tipo documento via UI.
    return await this.request('/api/invoices?documentType=issued');
  }

  /**
   * Get all proforma documents (28/02/2026 — non-fiscal twin of invoices).
   * Filters by documentType=proforma server-side. Always returns array.
   */
  async getProformas() {
    return await this.request('/api/invoices?documentType=proforma');
  }

  // Get next document number — supports both invoice and proforma.
  async getNextInvoiceNumber(managementType, year, documentType = "invoice") {
    const qs = new URLSearchParams();
    if (year) qs.set("year", String(year));
    if (documentType && documentType !== "invoice") qs.set("documentType", documentType);
    const tail = qs.toString();
    return await this.request(
      `/api/invoices/next-number/${managementType}${tail ? `?${tail}` : ""}`
    );
  }

  async createInvoice(invoice) {
    return await this.request('/api/invoices', {
      method: 'POST',
      body: JSON.stringify(invoice),
    });
  }

  async updateInvoice(id, invoice) {
    return await this.request(`/api/invoices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(invoice),
    });
  }

  async deleteInvoice(id) {
    return await this.request(`/api/invoices/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Convert a Documento Proforma into an official invoice (28/02/2026).
   * Returns the newly-created invoice with sourceProformaId / Number set.
   * Backend throws 409 on double-conversion attempts.
   */
  async convertProformaToInvoice(proformaId) {
    return await this.request(`/api/invoices/${proformaId}/convert-to-invoice`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  /**
   * Emit a Nota di Credito against an issued invoice (11/06/2026).
   * If `payload` is empty/null → full reversal (1:1 clone of amounts).
   * Otherwise pass {imponibile, iva, ivaAmount, totale, reason, notes}
   * for a partial credit note. Backend validates amounts <= remaining
   * headroom of the source invoice.
   * Throws on the usual 4xx (proforma source, double-credit chain).
   */
  async issueCreditNote(invoiceId, payload = null) {
    return await this.request(`/api/invoices/${invoiceId}/issue-credit-note`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  async deleteCreditNote(cnId) {
    return await this.request(`/api/credit-notes/${cnId}`, { method: 'DELETE' });
  }

  // Add payment to invoice
  async addInvoicePayment(invoiceId, payment) {
    return await this.request(`/api/invoices/${invoiceId}/payments`, {
      method: 'POST',
      body: JSON.stringify(payment),
    });
  }

  // Remove payment from invoice
  async removeInvoicePayment(invoiceId, paymentIndex) {
    return await this.request(`/api/invoices/${invoiceId}/payments/${paymentIndex}`, {
      method: 'DELETE',
    });
  }

  // Download invoice as PDF
  async downloadInvoicePDF(invoiceId) {
    const url = `${API_BASE_URL}/api/invoices/${invoiceId}/pdf`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });
      
      if (response.status === 401) {
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
        throw new Error(error.detail || 'Errore nel download del PDF');
      }
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'fattura.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      // Create blob and trigger download
      const blob = await response.blob();
      this._triggerDownload(blob, filename);
      
      return { success: true, filename };
    } catch (error) {
      console.error('PDF Download Error:', error);
      throw error;
    }
  }
  
  // Get invoice PDF as blob (for in-app viewer)
  async getInvoicePDFBlob(invoiceId) {
    const url = `${API_BASE_URL}/api/invoices/${invoiceId}/pdf`;
    
    console.log('[PDF] Fetching PDF blob for invoice:', invoiceId);
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });
      
      if (response.status === 401) {
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
        throw new Error(error.detail || 'Errore nel caricamento del PDF');
      }
      
      const blob = await response.blob();
      
      if (blob.size < 100) {
        throw new Error('PDF vuoto o corrotto');
      }
      
      console.log('[PDF] Blob fetched successfully, size:', blob.size);
      return blob;
    } catch (error) {
      console.error('[PDF] Error fetching blob:', error);
      throw error;
    }
  }

  // Open invoice PDF in a new tab for printing
  /**
   * PRINT INVOICE PDF - Deterministic approach
   * 1. Fetch PDF from backend
   * 2. Validate response (Content-Type, size)
   * 3. Open in new browser tab (window.open)
   * 4. Let user print manually or trigger auto-print with delay
   * 
   * This approach is reliable because:
   * - No hidden iframes (which have cross-origin print restrictions)
   * - No blob URL lifecycle issues
   * - User can always manually print if auto-print is blocked
   * - PDF is fully rendered in dedicated tab before print attempt
   */
  async printInvoicePDF(invoiceId) {
    const url = `${API_BASE_URL}/api/invoices/${invoiceId}/pdf`;
    
    console.log('[PRINT] Step 1: Fetching PDF from backend...', { invoiceId, url });
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });
      
      console.log('[PRINT] Step 2: Response received', { 
        status: response.status, 
        contentType: response.headers.get('Content-Type'),
        contentLength: response.headers.get('Content-Length')
      });
      
      if (response.status === 401) {
        console.error('[PRINT] Authentication failed');
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }
      
      if (!response.ok) {
        console.error('[PRINT] Server error', { status: response.status });
        const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
        throw new Error(error.detail || 'Errore nel caricamento del PDF');
      }
      
      // Validate Content-Type
      const contentType = response.headers.get('Content-Type');
      if (!contentType || !contentType.includes('application/pdf')) {
        console.error('[PRINT] Invalid Content-Type', { contentType });
        throw new Error('Risposta non valida: atteso PDF');
      }
      
      // Get blob and validate size
      const blob = await response.blob();
      console.log('[PRINT] Step 3: Blob created', { size: blob.size, type: blob.type });
      
      if (blob.size < 100) {
        console.error('[PRINT] PDF too small, likely corrupted', { size: blob.size });
        throw new Error('PDF vuoto o corrotto');
      }
      
      // Create blob URL
      const blobUrl = window.URL.createObjectURL(blob);
      console.log('[PRINT] Step 4: Blob URL created', { blobUrl: blobUrl.substring(0, 50) + '...' });
      
      // Open in new tab - this is the most reliable approach
      // It works in all browsers and allows manual print if auto-print is blocked
      console.log('[PRINT] Step 5: Opening PDF in new tab...');
      const printWindow = window.open(blobUrl, '_blank');
      
      // Reuse the office-spec filename from the backend's
      // Content-Disposition header so the popup-blocked fallback
      // matches the regular download path 1:1.
      const cd = response.headers.get('Content-Disposition') || '';
      const fnMatch = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
      const officeFilename = fnMatch ? fnMatch[1].trim() : `fattura_${invoiceId}.pdf`;

      if (!printWindow) {
        console.warn('[PRINT] Popup blocked, using fallback download');
        // Popup blocked - download the file instead
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = officeFilename;
        a.style.display = 'none';
        setTimeout(() => a.click(), 0);
        
        // Cleanup after download starts
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
        
        return { 
          success: true, 
          method: 'download',
          message: 'PDF scaricato. Aprilo per stampare.'
        };
      }
      
      console.log('[PRINT] Step 6: PDF tab opened, waiting for load...');
      
      // Wait for the PDF to fully load, then attempt auto-print
      // Use a generous delay since PDFs can take time to render
      printWindow.onload = () => {
        console.log('[PRINT] Step 7: PDF loaded in new tab');
        
        // Attempt auto-print after PDF is fully rendered
        setTimeout(() => {
          try {
            console.log('[PRINT] Step 8: Triggering print dialog...');
            printWindow.focus();
            printWindow.print();
            console.log('[PRINT] Print dialog triggered successfully');
          } catch (printError) {
            console.warn('[PRINT] Auto-print failed (user can print manually)', printError);
            // User can still print manually using Ctrl+P or browser menu
          }
        }, 1500); // 1.5 second delay for PDF rendering
      };
      
      // Cleanup blob URL after a long delay (give time for print)
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
        console.log('[PRINT] Cleanup: Blob URL revoked');
      }, 120000); // 2 minutes
      
      return { 
        success: true, 
        method: 'window',
        message: 'PDF aperto in nuova scheda'
      };
      
    } catch (error) {
      console.error('[PRINT] FATAL ERROR:', error);
      throw error;
    }
  }

  /**
   * PREVIEW INVOICE PDF (before save) - Deterministic approach
   * Uses window.open for reliable rendering
   */
  async previewInvoicePDF(invoiceData) {
    const url = `${API_BASE_URL}/api/invoices/preview-pdf`;
    
    console.log('[PREVIEW] Step 1: Generating preview PDF...', { 
      clientId: invoiceData.clientId,
      totale: invoiceData.totale 
    });
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(invoiceData),
      });
      
      console.log('[PREVIEW] Step 2: Response received', { 
        status: response.status,
        contentType: response.headers.get('Content-Type')
      });
      
      if (response.status === 401) {
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
        console.error('[PREVIEW] Server error', { detail: error.detail });
        throw new Error(error.detail || 'Errore nella generazione anteprima');
      }
      
      // Return blob for in-app modal preview (avoids popup blocks)
      const blob = await response.blob();
      console.log('[PREVIEW] Step 3: Blob created', { size: blob.size });
      
      if (blob.size < 100) {
        throw new Error('PDF anteprima vuoto o corrotto');
      }
      
      console.log('[PREVIEW] Success: Returning blob for in-app preview');
      return { success: true, blob };
      
    } catch (error) {
      console.error('[PREVIEW] FATAL ERROR:', error);
      throw error;
    }
  }

  // ==================== DEADLINES ====================
  async getDeadlines() {
    return await this.request('/api/deadlines');
  }

  async createDeadline(deadline) {
    return await this.request('/api/deadlines', {
      method: 'POST',
      body: JSON.stringify(deadline),
    });
  }

  async createDeadlinesBatch(deadlines) {
    return await this.request('/api/deadlines/batch', {
      method: 'POST',
      body: JSON.stringify(deadlines),
    });
  }

  async updateDeadline(id, deadline) {
    return await this.request(`/api/deadlines/${id}`, {
      method: 'PUT',
      body: JSON.stringify(deadline),
    });
  }

  async deleteDeadline(id) {
    return await this.request(`/api/deadlines/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== OPERATORS ====================
  async getOperators() {
    return await this.request('/api/operators');
  }

  async createOperator(operator) {
    return await this.request('/api/operators', {
      method: 'POST',
      body: JSON.stringify(operator),
    });
  }

  async updateOperator(id, operator) {
    return await this.request(`/api/operators/${id}`, {
      method: 'PUT',
      body: JSON.stringify(operator),
    });
  }

  async deleteOperator(id) {
    return await this.request(`/api/operators/${id}`, {
      method: 'DELETE',
    });
  }

  async createOperatorAccount(operatorId, password, username = null) {
    const body = { password };
    if (username) body.username = username;
    return await this.request(`/api/operators/${operatorId}/create-account`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async resetOperatorPassword(operatorId, newPassword) {
    return await this.request(`/api/operators/${operatorId}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ newPassword }),
    });
  }

  async deleteOperatorAccount(operatorId) {
    return await this.request(`/api/operators/${operatorId}/delete-account`, {
      method: 'DELETE',
    });
  }

  // ==================== NUMBERING ====================
  async getNumbering() {
    return await this.request('/api/numbering');
  }

  async getNextNumber(practiceType, year = null) {
    const params = year ? `?year=${year}` : '';
    return await this.request(`/api/numbering/next/${encodeURIComponent(practiceType)}${params}`);
  }

  async incrementNumbering(practiceType) {
    return await this.request(`/api/numbering/increment/${encodeURIComponent(practiceType)}`, {
      method: 'POST',
    });
  }

  // ==================== PRACTICE HISTORY ====================
  async getCaseHistory(caseId) {
    return await this.request(`/api/history/${caseId}`);
  }

  async getAllHistory() {
    return await this.request('/api/history');
  }

  async addHistoryEntry(entry) {
    return await this.request('/api/history', {
      method: 'POST',
      body: JSON.stringify(entry),
    });
  }

  // ==================== STUDIO SETTINGS ====================

  async getSettings() {
    return await this.request('/api/settings');
  }

  async updateSettings(settings) {
    return await this.request('/api/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Logo upload (uses FormData, not JSON)
  async uploadLogo(logoType, file) {
    const url = `${API_BASE_URL}/api/settings/logo/${logoType}`;
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
        body: formData,
      });
      
      if (response.status === 401) {
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
        throw new Error(error.detail || 'Errore nel caricamento');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Logo Upload Error:', error);
      throw error;
    }
  }

  // Get logo as blob URL for display
  async getLogoUrl(logoType) {
    const url = `${API_BASE_URL}/api/settings/logo/${logoType}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });
      
      if (!response.ok) {
        return null;
      }
      
      const blob = await response.blob();
      return window.URL.createObjectURL(blob);
    } catch (error) {
      console.error('Logo Fetch Error:', error);
      return null;
    }
  }

  // Delete logo
  async deleteLogo(logoType) {
    return await this.request(`/api/settings/logo/${logoType}`, {
      method: 'DELETE',
    });
  }

  // ==================== NOTIFICATIONS ====================
  async getNotifications({ unreadOnly = false, limit = 50 } = {}) {
    const params = new URLSearchParams();
    if (unreadOnly) params.set("unread_only", "true");
    if (limit) params.set("limit", String(limit));
    return await this.request(`/api/notifications?${params.toString()}`);
  }

  async markNotificationRead(notificationId) {
    return await this.request(`/api/notifications/${notificationId}/read`, { method: 'POST' });
  }

  async markAllNotificationsRead() {
    return await this.request(`/api/notifications/mark-all-read`, { method: 'POST' });
  }

  async markNotificationDone(notificationId) {
    return await this.request(`/api/notifications/${notificationId}/done`, { method: 'POST' });
  }

  async clearDoneNotifications({ olderThanDays = null } = {}) {
    const qs = olderThanDays ? `?olderThanDays=${olderThanDays}` : "";
    return await this.request(`/api/notifications/done${qs}`, { method: 'DELETE' });
  }

  /**
   * Snooze (rimanda) una notifica lato BACKEND.
   * Bug fix 22/02/2026: la persistenza dello snooze era solo in
   * localStorage del browser, perdendosi cross-device. Adesso scrive
   * `snoozedUntil` sul documento notifica del solo `recipientOperatorId`
   * corrente, così segue l'operatore su qualunque PC.
   *
   * @param {string} notificationId
   * @param {{minutes?: number, until?: string}} payload Either `minutes`
   *   (relative delay) or `until` (absolute ISO datetime).
   */
  async snoozeNotification(notificationId, payload) {
    return await this.request(`/api/notifications/${notificationId}/snooze`, {
      method: 'POST',
      body: JSON.stringify(payload || {}),
    });
  }

  async cancelNotificationSnooze(notificationId) {
    return await this.request(`/api/notifications/${notificationId}/snooze`, {
      method: 'DELETE',
    });
  }

  // ==================== FORM TEMPLATES (DYNAMIC MODULISTICA) ====================

  /**
   * Get all available form templates.
   */
  async getTemplates() {
    return await this.request('/api/templates');
  }

  /**
   * Get a specific template schema by ID.
   */
  async getTemplate(templateId) {
    return await this.request(`/api/templates/${templateId}`);
  }

  /**
   * Check if a dynamic template is available for a form type.
   * Used by feature flag system.
   */
  async checkDynamicTemplateAvailable(templateId) {
    return await this.request(`/api/templates/${templateId}/available`);
  }

  // ==================== SAVED FORMS (MODULISTICA) ====================

  async getForms(filters = {}) {
    const params = new URLSearchParams();
    if (filters.formType) params.append('form_type', filters.formType);
    if (filters.clientId) params.append('client_id', filters.clientId);
    const queryString = params.toString();
    return await this.request(`/api/forms${queryString ? `?${queryString}` : ''}`);
  }

  async getForm(id) {
    return await this.request(`/api/forms/${id}`);
  }

  async createForm(formData) {
    return await this.request('/api/forms', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  async updateForm(id, formData) {
    return await this.request(`/api/forms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(formData),
    });
  }

  async deleteForm(id) {
    return await this.request(`/api/forms/${id}`, {
      method: 'DELETE',
    });
  }

  async downloadFormPDF(formId) {
    const url = `${API_BASE_URL}/api/forms/${formId}/pdf`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });
      
      if (response.status === 401) {
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
        throw new Error(error.detail || 'Errore nel download del PDF');
      }
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'modulo.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      const blob = await response.blob();
      this._triggerDownload(blob, filename);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Form PDF Download Error:', error);
      throw error;
    }
  }

  // Download Mandato Pratica PDF from a case
  async downloadMandatoPDF(caseId) {
    const url = `${API_BASE_URL}/api/cases/${caseId}/mandato-pdf`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.token}`,
        },
      });
      
      if (response.status === 401) {
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
        throw new Error(error.detail || 'Errore nel download del PDF');
      }
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'mandato_pratica.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      const blob = await response.blob();
      this._triggerDownload(blob, filename);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Mandato PDF Download Error:', error);
      throw error;
    }
  }

  // ─── Stampa Fascicolo Pratica (28/06/2026) ─────────────────────────
  // Scarica il PDF "Fascicolo Pratica" — snapshot completo della
  // scheda pratica con anagrafica cliente, dati, date, operatori,
  // compensi, documenti collegati, scadenze, note, chiusura e
  // cronologia completa (audit trail) ordinata cronologicamente.
  async downloadPracticePDF(caseId) {
    const url = `${API_BASE_URL}/api/cases/${caseId}/practice-pdf`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${this.token}` },
      });
      if (response.status === 401) {
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
        throw new Error(error.detail || 'Errore nel download del fascicolo pratica');
      }
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'fascicolo_pratica.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1].replace(/"/g, '');
      }
      const blob = await response.blob();
      this._triggerDownload(blob, filename);
      return { success: true, filename };
    } catch (error) {
      console.error('Practice PDF Download Error:', error);
      throw error;
    }
  }

  // Print Mandato Pratica from current form data (without needing saved case)
  async printMandatoFromForm(formData) {
    const url = `${API_BASE_URL}/api/mandato/print-from-form`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.status === 401) {
        this._handleSessionExpired();
        throw new Error('Sessione scaduta');
      }
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: 'Errore sconosciuto' }));
        throw new Error(error.detail || 'Errore nella generazione del PDF');
      }
      
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'mandato_pratica.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      const blob = await response.blob();
      this._triggerDownload(blob, filename);
      
      return { success: true, filename };
    } catch (error) {
      console.error('Mandato PDF Print Error:', error);
      throw error;
    }
  }
  // ==================== REPORTS ====================

  async getInvoicesSummary(year = null, month = null, management = null) {
    const params = new URLSearchParams();
    if (year) params.set('year', year);
    if (month) params.set('month', month);
    if (management) params.set('management', management);
    const qs = params.toString();
    return this.request(`/api/reports/invoices-summary${qs ? '?' + qs : ''}`);
  }

  async getInvoicesDetail(filters = {}) {
    const params = new URLSearchParams();
    if (filters.year) params.set('year', filters.year);
    if (filters.month) params.set('month', filters.month);
    if (filters.management) params.set('management', filters.management);
    if (filters.paymentStatus) params.set('payment_status', filters.paymentStatus);
    if (filters.hasResiduo) params.set('has_residuo', 'true');
    if (filters.hasPayments) params.set('has_payments', 'true');
    if (filters.clientId) params.set('client_id', filters.clientId);
    const qs = params.toString();
    return this.request(`/api/reports/invoices-detail${qs ? '?' + qs : ''}`);
  }

  async getInvoicesByClient(year = null, month = null, management = null) {
    const params = new URLSearchParams();
    if (year) params.set('year', year);
    if (month) params.set('month', month);
    if (management) params.set('management', management);
    const qs = params.toString();
    return this.request(`/api/reports/invoices-by-client${qs ? '?' + qs : ''}`);
  }

  async getPracticesSummary(year = null, month = null, management = null) {
    const params = new URLSearchParams();
    if (year) params.set('year', year);
    if (month) params.set('month', month);
    if (management) params.set('management', management);
    const qs = params.toString();
    return this.request(`/api/reports/practices-summary${qs ? '?' + qs : ''}`);
  }

  async getPracticesByClient(year = null, month = null, management = null) {
    const params = new URLSearchParams();
    if (year) params.set('year', year);
    if (month) params.set('month', month);
    if (management) params.set('management', management);
    const qs = params.toString();
    return this.request(`/api/reports/practices-by-client${qs ? '?' + qs : ''}`);
  }

  async getPracticesDetail(filters = {}) {
    const params = new URLSearchParams();
    if (filters.year) params.set('year', filters.year);
    if (filters.month) params.set('month', filters.month);
    if (filters.management) params.set('management', filters.management);
    if (filters.statusFilter) params.set('status_filter', filters.statusFilter);
    if (filters.clientId) params.set('client_id', filters.clientId);
    const qs = params.toString();
    return this.request(`/api/reports/practices-detail${qs ? '?' + qs : ''}`);
  }

  /**
   * Riepilogo operativo aggregato per singolo cliente (27/02/2026).
   * Unifica practices + Diario Giornaliero + Lavoro Quotidiano.
   * Accessibile a tutti i ruoli.
   * Filtri periodo opzionali (server-side): { from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' }.
   */
  async getClientReport(clientId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.from) params.set("from", filters.from);
    if (filters.to) params.set("to", filters.to);
    const qs = params.toString();
    return this.request(`/api/client-report/${clientId}${qs ? "?" + qs : ""}`);
  }

  // ==================== SOLLECITI ====================

  async getSolleciti(year = null, management = null) {
    const params = new URLSearchParams();
    if (year) params.set('year', year);
    if (management) params.set('management', management);
    const qs = params.toString();
    return this.request(`/api/reports/solleciti${qs ? '?' + qs : ''}`);
  }

  async sendReminder(invoiceId, data) {
    return this.request(`/api/reports/solleciti/${invoiceId}/send`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getReminderHistory(invoiceId) {
    return this.request(`/api/reports/solleciti/${invoiceId}/history`);
  }

  // ==================== CONTACTS (Rubrica Clienti) ====================

  async getContacts() {
    return this.request('/api/contacts');
  }

  async getContactsByCase(caseId) {
    return this.request(`/api/contacts/by-case/${caseId}`);
  }

  async createContact(contactData) {
    return this.request('/api/contacts', {
      method: 'POST',
      body: JSON.stringify(contactData),
    });
  }

  async updateContact(id, contactData) {
    return this.request(`/api/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(contactData),
    });
  }

  async deleteContact(id) {
    return this.request(`/api/contacts/${id}`, {
      method: 'DELETE',
    });
  }

  // Mark a diary entry as completed. `completedAt` is optional (YYYY-MM-DD);
  // backend defaults to today UTC. The endpoint is idempotent.
  async completeContact(id, completedAt = "") {
    return this.request(`/api/contacts/${id}/complete`, {
      method: 'POST',
      body: JSON.stringify({ completedAt }),
    });
  }

  // Reopen a previously-completed diary entry.
  async reopenContact(id) {
    return this.request(`/api/contacts/${id}/reopen`, {
      method: 'POST',
    });
  }

  // Append an internal operational reply to a diary note (S1 Connected-
  // Modules). The server stores it as a `history[] action=reply` row
  // and fans out a `diary_reply` notification to every thread participant
  // except the current actor.
  async replyToContact(id, content) {
    return this.request(`/api/contacts/${id}/reply`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }


  // ============== DAILY PRACTICES (Pratiche Quotidiane) ==============

  async listDailyPractices() {
    return this.request('/api/daily-practices');
  }

  async getDailyPractice(id) {
    return this.request(`/api/daily-practices/${id}`);
  }

  async createDailyPractice(data) {
    return this.request('/api/daily-practices', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // S3 Connected-Modules — recupera i lavori creati a partire da una
  // voce di un altro modulo (es. una nota Diario convertita).
  async getDailyPracticesBySource(sourceModule, sourceId) {
    return this.request(
      `/api/daily-practices/by-source/${encodeURIComponent(sourceModule)}/${encodeURIComponent(sourceId)}`
    );
  }

  async updateDailyPractice(id, data) {
    return this.request(`/api/daily-practices/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteDailyPractice(id) {
    return this.request(`/api/daily-practices/${id}`, {
      method: 'DELETE',
    });
  }

  async addDailyPracticeNote(id, note) {
    return this.request(`/api/daily-practices/${id}/note`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    });
  }

  /**
   * Append-only "Nota operativa aggiuntiva" su una Pratica Quotidiana
   * (28/02/2026). Distinta dalla "Nota rapida" (history), questa
   * conserva una linea temporale di dettagli operativi aggiunti DOPO
   * la creazione del record, con autore e timestamp.
   */
  async addDailyPracticeOperationalNote(id, text) {
    return this.request(`/api/daily-practices/${id}/op-note`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    });
  }

  // ==================== ACTIVITY LOG (Registro Attività - P1) ====================
  // Vista aggregata read-only su daily_practices + client_contacts.
  // Caller passa `filters = { clientId, fromDate, toDate, managementType,
  // area, operatorId, status, sources: "lavoro,diario", groupBy: "client" }`.
  async getActivityLog(filters = {}) {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") params.append(k, v);
    });
    const qs = params.toString();
    return this.request(`/api/activity-log${qs ? `?${qs}` : ""}`);
  }

  // ==================== CUSTOM DEADLINES (Scadenze Extra) ====================

  async getCustomDeadlines() {
    return this.request('/api/custom-deadlines');
  }

  // S2 Connected-Modules — recupera tutte le scadenze linkate ad una
  // voce di un altro modulo (es. una nota Diario). Read-only.
  async getCustomDeadlinesBySource(sourceModule, sourceId) {
    return this.request(
      `/api/custom-deadlines/by-source/${encodeURIComponent(sourceModule)}/${encodeURIComponent(sourceId)}`
    );
  }

  async createCustomDeadline(data) {
    return this.request('/api/custom-deadlines', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCustomDeadline(id, data) {
    return this.request(`/api/custom-deadlines/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCustomDeadline(id) {
    return this.request(`/api/custom-deadlines/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Push the next reminder for a custom deadline to tomorrow.
   * Used by the NotificationBell popup "Rimanda" button.
   */
  async snoozeCustomDeadline(id) {
    return this.request(`/api/custom-deadlines/${id}/snooze`, {
      method: 'POST',
    });
  }

  /**
   * Toggle a custom deadline's status to "completata". The backend
   * automatically clears `nextReminderAt` so the scheduler stops
   * generating new popups for it.
   */
  async completeCustomDeadline(id, current) {
    return this.request(`/api/custom-deadlines/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...current, status: 'completata' }),
    });
  }

  // ==================== BILLING PLANS ====================

  async getBillingPlans(clientId = null) {
    const params = clientId ? `?clientId=${clientId}` : '';
    return this.request(`/api/billing-plans${params}`);
  }

  async getBillingPlansByClient(clientId) {
    return this.request(`/api/billing-plans/client/${clientId}`);
  }

  async createBillingPlan(data) {
    return this.request('/api/billing-plans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateBillingPlan(id, data) {
    return this.request(`/api/billing-plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteBillingPlan(id) {
    return this.request(`/api/billing-plans/${id}`, {
      method: 'DELETE',
    });
  }

  async generateBillingItems() {
    return this.request('/api/billing-plans/generate', {
      method: 'POST',
    });
  }

  async getBillingItems() {
    return this.request('/api/billing-items');
  }

  async toggleBillingItemStatus(id) {
    return this.request(`/api/billing-items/${id}`, {
      method: 'PUT',
    });
  }

  // ==================== EMAIL TEMPLATE ====================

  async getEmailTemplate() {
    return this.request('/api/settings/email-template');
  }

  // Multi-template (17/07/2026 — v1.4.1)
  async getEmailTemplates() {
    return this.request('/api/settings/email-templates');
  }

  async saveEmailTemplate(template) {
    return this.request('/api/settings/email-templates', {
      method: 'POST',
      body: JSON.stringify(template),
    });
  }

  async deleteEmailTemplate(templateId) {
    return this.request(`/api/settings/email-templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  // ==================== CLIENTE 360° ====================

  async getClientTimeline(clientId) {
    return this.request(`/api/clients/${clientId}/timeline`);
  }

  async downloadClientTimelinePDF(clientId, clientName = 'Cliente') {
    // Streaming download via fetch → blob (l'endpoint restituisce PDF binary)
    const response = await fetch(`${API_BASE_URL}/api/clients/${clientId}/timeline/pdf`, {
      headers: { 'Authorization': `Bearer ${this.token}` },
    });
    if (!response.ok) {
      const errText = await response.text();
      throw new Error(errText || `HTTP ${response.status}`);
    }
    const blob = await response.blob();
    const safe = String(clientName).replace(/[^a-zA-Z0-9À-ÿ_\-\s]/g, '').trim().replace(/\s+/g, '_') || 'Cliente';
    const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const filename = `cliente_360_${safe}_${dateStr}.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return { filename };
  }

  // ==================== BACKUP & RESTORE ====================

  async createBackup() {
    // Direct browser navigation — reliable on mobile and desktop
    const url = `${API_BASE_URL}/api/backup/download?token=${encodeURIComponent(this.token)}`;
    window.location.href = url;
    return { message: 'Backup scaricato' };
  }

  async listBackups() {
    return this.request('/api/backup/list');
  }

  async getBackupInfo() {
    return this.request('/api/backup/info');
  }

  async createManualBackup() {
    return this.request('/api/backup/manual', { method: 'POST' });
  }

  async validateBackup(filename) {
    return this.request(`/api/backup/validate/${encodeURIComponent(filename)}`, { method: 'POST' });
  }

  async deleteBackup(filename) {
    return this.request(`/api/backup/${encodeURIComponent(filename)}`, { method: 'DELETE' });
  }

  async downloadBackupFile(filename) {
    const url = `${API_BASE_URL}/api/backup/download/${encodeURIComponent(filename)}?token=${encodeURIComponent(this.token)}`;
    window.location.href = url;
  }

  async restoreBackup(file) {
    const url = `${API_BASE_URL}/api/backup/restore`;
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${this.token}` },
      body: formData
    });
    if (response.status === 401) {
      this._handleSessionExpired();
      throw new Error('Sessione scaduta');
    }
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.detail || 'Errore durante il ripristino');
    }
    return data;
  }

}

export default new APIClient();
